import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

interface Item { slug: string; flag: string; countryEn: string; authorityShort: string; certName: string }

export const Route = createFileRoute("/en/police-clearance/")({
  loader: async (): Promise<{ items: Item[] }> => {
    const [{ POLICE_CLEARANCE_COUNTRIES }, { PC_COPY_EN }] = await Promise.all([
      import("@/content/police-clearance/registry"),
      import("@/content/police-clearance/registry-en"),
    ]);
    return {
      items: POLICE_CLEARANCE_COUNTRIES.filter((c) => PC_COPY_EN[c.slug]).map((c) => ({
        slug: c.slug,
        flag: c.flag,
        countryEn: c.countryEn,
        authorityShort: c.authorityShort,
        certName: c.certName,
      })),
    };
  },
  head: () => {
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: "Police Clearance Certificates from Thailand",
        description:
          "Obtain FBI, ACRO, AFP, RCMP, NZ Ministry of Justice, Singapore, Japanese and Korean police certificates from Phuket — fingerprinting, filing, Apostille, certified translation and Thai MFA legalisation.",
        path: "/en/police-clearance",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/police-clearance" }],
        keywords: [
          "police clearance certificate thailand",
          "fbi background check thailand",
          "acro police certificate thailand",
          "afp police check from thailand",
          "rcmp criminal record check thailand",
        ],
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/police-clearance")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Police Clearance", url: `${site}/en/police-clearance` },
            ]),
          ),
        },
      ],
    };
  },
  component: PoliceClearanceHubEn,
});

function PoliceClearanceHubEn() {
  const { items } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Police Clearance</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">Police clearance certificates handled from Thailand</h1>
        <p data-speakable="true" className="speakable-summary mb-6 max-w-3xl text-muted-foreground">
          Most foreign police certificates can be obtained without leaving Thailand. Our Phuket team takes the
          fingerprint cards where they are required, files with the issuing authority through accredited channels or a
          power of attorney, then arranges the Apostille or consular legalisation, certified translation and Thai MFA
          endorsement your receiving authority asks for.
        </p>
        <div className="mb-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/police-clearance" className="text-primary underline">ตรวจประวัติอาชญากรรมต่างประเทศ →</a>
        </div>
        <section aria-label="Countries" className="mb-10">
          <h2 className="mb-3 text-lg font-semibold">Choose the issuing country</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {items.map((c: Item) => (
              <a
                key={c.slug}
                href={`/en/police-clearance/${c.slug}`}
                className="rounded-lg border bg-card p-4 text-sm transition hover:border-primary hover:shadow-sm"
              >
                <div className="font-semibold">
                  <span className="mr-2 text-lg">{c.flag}</span>
                  {c.authorityShort} — {c.countryEn}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{c.certName}</div>
              </a>
            ))}
          </div>
        </section>
        <section className="mb-10">
          <h2 className="mb-3 text-lg font-semibold">Thai police clearance and related services</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/police-clearance/faq">Police clearance FAQ</a></li>
            <li><a className="text-primary underline" href="/en/consular-legalization/faq">MFA legalisation &amp; Apostille</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation</a></li>
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial services &amp; POA</a></li>
          </ul>
        </section>
        <section className="rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Get a case-specific quote</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Fees vary with the authority fee, legalisation route and courier, so we quote per case. Contact us on
            {" "}{COMPANY.phone}, LINE {COMPANY.lineId} or {COMPANY.email} — Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact the team →</a>
        </section>
        <FaqSourceNote topic="police-clearance" locale="en" />
      </main>
      <SiteFooter />
    </div>
  );
}
